import posthog from 'posthog-js'
import type { SubmitEvent } from 'react'
import { useRef, useState } from 'react'
import {
  type ContactFormFieldName,
  type ContactFormValues,
  contactFormPayloadSchema,
  getDefaultContactFormValues,
} from '@/features/contact/model/contactFormSchema'
import { createLeadFormTrackingPayload } from '@/features/contact/model/createLeadFormTrackingPayload'
import { submitContactForm } from '@/features/contact/model/submitContactForm'
import { gtmEvent } from '@/shared/lib/googleTag'

type SubmissionState = 'error' | 'idle' | 'submitting' | 'success'
type FieldErrors = Partial<Record<ContactFormFieldName, string>>

export function useContactForm() {
  const startedAtRef = useRef(Date.now())
  const [values, setValues] = useState<ContactFormValues>(getDefaultContactFormValues)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [formMessage, setFormMessage] = useState<string | null>(null)
  const [submissionState, setSubmissionState] = useState<SubmissionState>('idle')

  function handleFieldChange(field: keyof ContactFormValues, value: string) {
    setValues((currentValues) => ({
      ...currentValues,
      [field]: value,
    }))

    if (field !== 'website') {
      setFieldErrors((currentErrors) => ({
        ...currentErrors,
        [field]: undefined,
      }))
    }

    if (submissionState !== 'idle') {
      setSubmissionState('idle')
      setFormMessage(null)
    }
  }

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()

    const parsedPayload = contactFormPayloadSchema.safeParse({
      ...values,
      startedAt: startedAtRef.current,
    })

    if (!parsedPayload.success) {
      const nextFieldErrors = parsedPayload.error.issues.reduce<FieldErrors>((errors, issue) => {
        const field = issue.path[0]

        if (field === 'company' || field === 'email' || field === 'message' || field === 'name' || field === 'reason') {
          errors[field] = issue.message
        }

        return errors
      }, {})

      setFieldErrors(nextFieldErrors)
      setFormMessage('Fix the marked fields and try again.')
      setSubmissionState('error')
      return
    }

    setSubmissionState('submitting')
    setFormMessage(null)

    const result = await submitContactForm(parsedPayload.data)

    if (!result.ok) {
      setFieldErrors(result.fieldErrors ?? {})
      setFormMessage(getErrorMessage(result.code, result.error))
      setSubmissionState('error')
      posthog.capture('contact_form_submission_failed', {
        error_code: result.code,
        reason: parsedPayload.data.reason,
      })
      return
    }

    setValues(getDefaultContactFormValues())
    setFieldErrors({})
    setFormMessage('Message sent.')
    setSubmissionState('success')
    startedAtRef.current = Date.now()

    gtmEvent('lead_form_submit', createLeadFormTrackingPayload(parsedPayload.data))
    posthog.capture('contact_form_submitted', {
      reason: parsedPayload.data.reason,
      time_to_submit_ms: Date.now() - parsedPayload.data.startedAt,
    })
  }

  return {
    fieldErrors,
    formMessage,
    handleFieldChange,
    handleSubmit,
    isSubmitting: submissionState === 'submitting',
    submissionState,
    values,
  }
}

function getErrorMessage(code?: string, error?: string) {
  if (code === 'DELIVERY_UNAVAILABLE') {
    return 'The form is not wired for delivery yet. Use direct email until SMTP is configured.'
  }

  return error ?? 'That did not send. Try again or use direct email.'
}
